import * as React from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import Avatar from 'antd/lib/avatar';
import Flex from 'antd/lib/flex';
import Text from 'antd/lib/typography/Text';
import Tooltip from 'antd/lib/tooltip';
import { SettingOutlined } from '@ant-design/icons';
import { RootState } from '../../../reducers/reducers';
import { selectAppGroup } from '../../../selectors/selectors';
import { selectAppsInGroup } from '../../../selectors/memoizedSelectors';
import {
    selectAvailableDestinations,
    selectIsDestinationsDataLoadedForApp,
    selectShouldRenderWorkflowsOverviewForApp,
} from '../../../selectors/summaryViewSelectors';
import { Destination } from '../../../types/destinations';
import { AppPlatform } from '../../../types';
import { displayTextForAppPlatform } from '../../../helpers/displayHelpers';
import uniq from 'lodash/uniq';
import { navFlexItemStyle } from '../../styles/globalStyles';
import { HoverableNavItem } from '../shared/HoverableNavItem';
import { useSidebarColors } from '@/components/styles/themeMode';
import { getMdmPlatformOverride } from '../../../helpers/temporaryMdmDisplayOverride';

export interface AppGroupRowProps {
    orgId: string;
    appGroupId: string;
    isSelected: boolean;
    onSelect: () => void;
    onSettingsClick: (appIdParam: string) => void;
}

export const AppGroupRow: React.FC<AppGroupRowProps> = ({
    orgId,
    appGroupId,
    isSelected,
    onSelect,
    onSettingsClick,
}) => {
    const sc = useSidebarColors();
    const appGroup = useSelector((state: RootState) => selectAppGroup(state, appGroupId));
    const appsInGroup = useSelector((state: RootState) => selectAppsInGroup(state, appGroupId));

    const firstApp = appsInGroup[0];

    const hasMultiplePlatforms = appsInGroup.length > 1;

    const destinationsByAppId = useSelector(
        (state: RootState) =>
            appsInGroup.reduce(
                (acc, app) => {
                    acc[app.id] = selectAvailableDestinations(state, orgId, app.id);
                    return acc;
                },
                {} as Record<string, Destination[]>,
            ),
        shallowEqual,
    );

    const isWorkflowsMigratedByAppId = useSelector(
        (state: RootState) =>
            appsInGroup.reduce(
                (acc, app) => {
                    acc[app.id] = selectShouldRenderWorkflowsOverviewForApp(state, orgId, app.id);
                    return acc;
                },
                {} as Record<string, boolean>,
            ),
        shallowEqual,
    );

    const isDestinationsLoaded = useSelector((state: RootState) =>
        firstApp ? selectIsDestinationsDataLoadedForApp(state, orgId, firstApp.id) : false,
    );

    const singleAppDestinations = !hasMultiplePlatforms && firstApp ? (destinationsByAppId[firstApp.id] ?? []) : [];

    const singleAppPlatformText = React.useMemo(() => {
        if (hasMultiplePlatforms || !firstApp) return null;
        const mdmOverride = getMdmPlatformOverride(orgId, firstApp.appId);
        if (mdmOverride) return mdmOverride.label;
        if (!isDestinationsLoaded) return null;
        const isWorkflowsMigrated = isWorkflowsMigratedByAppId[firstApp.id] ?? false;
        if (!isWorkflowsMigrated) {
            return displayTextForAppPlatform(firstApp.appSettings.platform, firstApp.appSettings.appStoreType);
        }
        if (singleAppDestinations.length === 0) return null;
        const uniquePlatforms = uniq<AppPlatform>(singleAppDestinations.map((d) => d.platform));
        return uniquePlatforms.map((p) => displayTextForAppPlatform(p)).join(' / ');
    }, [
        hasMultiplePlatforms,
        firstApp,
        orgId,
        isWorkflowsMigratedByAppId,
        singleAppDestinations,
        isDestinationsLoaded,
    ]);

    const allAppsRestricted = appsInGroup.length > 0 && appsInGroup.every((app) => app.isRestrictedApp);

    if (!appGroup || !firstApp) return null;

    const imageUrl = appGroup.appGroupImageUrl || firstApp.appSettings.appImageUrl;
    const displayName = appGroup.name || firstApp.appSettings.appName;

    const handleSettingsClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        onSettingsClick(firstApp.appId);
    };

    return (
        <HoverableNavItem
            isSelected={isSelected}
            isDisabled={allAppsRestricted}
            onClick={onSelect}
            style={{
                top: 0,
                position: 'sticky',
                zIndex: 1,
            }}
        >
            <Flex
                align="center"
                style={{
                    ...navFlexItemStyle,
                    gap: 12,
                    backgroundColor: 'inherit',
                    marginRight: 8,
                }}
            >
                <Avatar
                    shape="square"
                    size={32}
                    src={imageUrl}
                    style={{ borderRadius: 6, border: 'none', flexShrink: 0 }}
                >
                    {displayName?.charAt(0).toUpperCase()}
                </Avatar>
                <Flex align="center" style={navFlexItemStyle}>
                    <Text
                        style={{
                            color: sc.text,
                            fontWeight: isSelected ? 600 : 400,
                            flex: 1,
                            minWidth: 0,
                        }}
                        ellipsis
                    >
                        {displayName}
                        {singleAppPlatformText && (
                            <span style={{ color: sc.platformMuted, marginLeft: 4, fontWeight: 400 }}>
                                | {singleAppPlatformText}
                            </span>
                        )}
                    </Text>
                </Flex>
            </Flex>
            {!hasMultiplePlatforms && (
                <Tooltip title="Settings" mouseEnterDelay={0.4}>
                    <SettingOutlined
                        style={{ color: sc.icon, fontSize: 16, cursor: 'pointer' }}
                        onClick={handleSettingsClick}
                    />
                </Tooltip>
            )}
        </HoverableNavItem>
    );
};
